import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { HseEventForm } from "../../../../components/dashboard/hse/hseEventForm";
import { fetchHseEvent } from "../../../../api/hse/events";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { EventSubnav } from "../../../../components/dashboard/hse/hseEventTabs";

/**
 * HSE/KS Event Edit - Create or edit event (ISO 9001:2015 compliant)
 */
export const HseKsEventEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(false);

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/deviations")) return "deviations";
    if (path.includes("/near-misses")) return "near-misses";
    if (path.includes("/accidents")) return "accidents";
    if (path.includes("/complaints")) return "complaints";
    if (path.includes("/observations")) return "observations";
    return "all";
  });


  // Determine default event type/category based on URL or query params
  const getDefaultEventData = () => {
    const type = searchParams.get("type");
    const defaults = {};

    switch (type) {
      case "deviation":
        defaults.event_category = "law_violation";
        break;
      case "near-miss":
        defaults.event_type = "near_miss";
        break;
      case "accident":
        defaults.event_type = "injury";
        break;
      case "complaint":
        defaults.event_category = "other";
        break;
      case "observation":
        defaults.event_category = "information";
        break;
      default:
        // Use activeTab if no type param
        switch (activeTab) {
          case "deviations":
            defaults.event_category = "law_violation";
            break;
          case "near-misses":
            defaults.event_type = "near_miss";
            break;
          case "accidents":
            defaults.event_type = "injury";
            break;
          case "complaints":
            defaults.event_category = "other";
            break;
          case "observations":
            defaults.event_category = "information";
            break;
        }
    }

    return defaults;
  };

  useEffect(() => {
    if (!isNew && id) {
      const fetchEventData = async () => {
        try {
          setLoading(true);
          const response = await fetchHseEvent(id);
          setEvent(response.data.data);
        } catch (err) {
          console.error("Error fetching HSE event:", err);
          setError(true);
          toast.error(t("Kunne ikke laste hendelse"));
        } finally {
          setLoading(false);
        }
      };
      fetchEventData();
    } else if (isNew) {
      // Set default values for new event based on type
      const defaults = getDefaultEventData();
      setEvent(defaults);
      setLoading(false);
    }
  }, [id, isNew]);

  const handleSubmit = async (eventData) => {
    try {
      toast.success(
        isNew ? t("Hendelse ble opprettet") : t("Hendelse ble oppdatert")
      );
      // Navigate to view page
      navigate(`/dashboard/hse-ks/events/${eventData.id || eventData.data?.id}`);
    } catch (err) {
      toast.error(t("Kunne ikke lagre hendelse"));
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/hse-ks/events");
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste hendelse")]} />;
  }

  return (
    <HsePageChrome
      title={isNew ? "Ny hendelse" : `Rediger hendelse #${id}`}
      subtitle="Hendelsesskjema (ISO 9001:2015 - kap. 10 Forbedring)"
      backTo={isNew ? "/dashboard/hse-ks/events" : `/dashboard/hse-ks/events/${id}`}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <EventSubnav active={activeTab} />

          {/* Event Form */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <HseEventForm
                event={event}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

